import React, { useEffect, useRef, useState } from 'react';

type ModuleId =
  | 'admissions'
  | 'fees'
  | 'exams'
  | 'security'
  | 'communication'
  | 'transport'
  | 'website'
  | 'ai'
  | 'analytics'
  | 'ops'
  | 'multicampus';

type CategoryId = 'people' | 'campus' | 'reach' | 'platform';

type ExplorerModule = {
  id: ModuleId;
  category: CategoryId;
  label: string;
  short: string;
  blurb: string;
  outcome: string;
  stats: Array<{ label: string; value: string }>;
  rows: Array<{ primary: string; secondary: string; badge: string }>;
};

const CATEGORIES: Array<{ id: CategoryId; label: string; hint: string }> = [
  { id: 'people', label: 'People & money', hint: 'Enrol, collect, certify' },
  { id: 'campus', label: 'Campus day', hint: 'Gate, bus, daily ops' },
  { id: 'reach', label: 'Reach parents', hint: 'WhatsApp, web, AI' },
  { id: 'platform', label: 'Platform', hint: 'Insights & multi-campus' },
];

const MODULES: ExplorerModule[] = [
  {
    id: 'admissions',
    category: 'people',
    label: 'Admissions & students',
    short: 'Admissions',
    blurb: 'Enquiry → follow-up → counselling → convert, then one roster for profiles, sections, and session promotion.',
    outcome: 'From walk-in to active roll in one flow',
    stats: [
      { label: 'Active', value: '1,248' },
      { label: 'Enquiries', value: '64' },
      { label: 'Converted', value: '28' },
    ],
    rows: [
      { primary: 'Walk-in · Class 1', secondary: 'Counselling booked · Fri', badge: 'Hot' },
      { primary: 'Aarav Sharma', secondary: 'Class 10-A · Roll 12', badge: 'Active' },
      { primary: 'Session promotion', secondary: '2025–26 → 2026–27', badge: 'Ready' },
    ],
  },
  {
    id: 'fees',
    category: 'people',
    label: 'Fees',
    short: 'Fees',
    blurb: 'Structures, collections, reminders, receipts, and waiver approval queues — fewer spreadsheet chases for accounts.',
    outcome: 'Collections and reminders without spreadsheet chase',
    stats: [
      { label: 'Collected', value: '₹18.4L' },
      { label: 'Pending', value: '₹2.1L' },
      { label: 'Waivers', value: '7' },
    ],
    rows: [
      { primary: 'Term II · Class 9', secondary: 'Due 15 Aug', badge: 'Open' },
      { primary: 'Transport · Zone B', secondary: 'Monthly', badge: 'Recurring' },
      { primary: 'Fee waiver · hardship', secondary: 'Docs attached', badge: 'Queue' },
    ],
  },
  {
    id: 'exams',
    category: 'people',
    label: 'Exams & certificates',
    short: 'Exams',
    blurb: 'Marks, report cards, fee-gated admit cards, and QR-verifiable certificates so forged TC / bonafide / receipts stop here.',
    outcome: 'Admit cards and QR-verified certificates',
    stats: [
      { label: 'Admit cards ready', value: '891' },
      { label: 'Fee-blocked', value: '117' },
      { label: 'QR verifies', value: '240' },
    ],
    rows: [
      { primary: 'Half-yearly admit cards', secondary: 'Fees clear → generate', badge: 'Campaign' },
      { primary: 'Bonafide · Diya Patel', secondary: 'Scan to verify authenticity', badge: 'Verified' },
      { primary: 'Transfer pack', secondary: 'TC · marksheet · fee NOC', badge: 'Issued' },
    ],
  },
  {
    id: 'security',
    category: 'campus',
    label: 'Gate & security',
    short: 'Gate',
    blurb: 'Guardian pickup by phone or admission no., authorized match only, daily PIN/QR passes, and a clean handover log.',
    outcome: 'Authorized pickup with PIN / QR handover',
    stats: [
      { label: 'Pickups today', value: '186' },
      { label: 'PIN / QR issued', value: '142' },
      { label: 'Mismatches blocked', value: '3' },
    ],
    rows: [
      { primary: 'Kabir Singh · Class 12-C', secondary: 'Mother · +91 ·•••• 4821', badge: 'Matched' },
      { primary: 'Daily pickup PIN', secondary: 'Valid until gate close', badge: 'Active' },
      { primary: 'Bus arrival window', secondary: 'Route North-2 · boarding', badge: 'Live' },
    ],
  },
  {
    id: 'transport',
    category: 'campus',
    label: 'Live bus & routes',
    short: 'Bus',
    blurb: 'Admin routes and vehicles plus parent-portal live location (~15s refresh) so “bus late?” calls drop.',
    outcome: 'Parents see the bus — fewer late calls',
    stats: [
      { label: 'Routes', value: '16' },
      { label: 'On road', value: '14' },
      { label: 'Riders', value: '640' },
    ],
    rows: [
      { primary: 'Route North-2', secondary: 'Parent map · ~15s refresh', badge: 'Live' },
      { primary: 'Route City Loop', secondary: 'Afternoon pickup', badge: 'Boarding' },
      { primary: 'Route East-1', secondary: 'Standby', badge: 'Idle' },
    ],
  },
  {
    id: 'ops',
    category: 'campus',
    label: 'Attendance & ops',
    short: 'Ops',
    blurb: 'Class attendance, homework, library, hostel, inventory, and procurement from requisition → PO → GRN → payment.',
    outcome: 'Attendance through procurement in one ops lane',
    stats: [
      { label: 'Present today', value: '94%' },
      { label: 'Open POs', value: '11' },
      { label: 'Library loans', value: '208' },
    ],
    rows: [
      { primary: 'Class 7-A attendance', secondary: 'Period 1 · Homeroom', badge: 'Done' },
      { primary: 'Lab chemicals · PO-214', secondary: 'GRN pending', badge: 'Procurement' },
      { primary: 'Hostel · Block B', secondary: 'Occupancy 92%', badge: 'Live' },
    ],
  },
  {
    id: 'communication',
    category: 'reach',
    label: 'WhatsApp & messaging',
    short: 'WhatsApp',
    blurb: 'Meta Business onboarding, bulk by class or sheet, inbox, delivery logs, and cost tracking on the parent line you own.',
    outcome: 'Parent line you own — with cost visibility',
    stats: [
      { label: 'Sent today', value: '1,240' },
      { label: 'Delivered', value: '98%' },
      { label: 'Spend', value: '₹1,860' },
    ],
    rows: [
      { primary: 'Fee reminder · WhatsApp', secondary: 'Pending payers · Class 6–10', badge: 'Sent' },
      { primary: 'PTM notice', secondary: 'Parents · Classes 1–5', badge: 'Queued' },
      { primary: 'Delivery + cost log', secondary: 'Know what messaging costs', badge: 'Tracked' },
    ],
  },
  {
    id: 'website',
    category: 'reach',
    label: 'School website',
    short: 'Website',
    blurb: 'Public CMS — theme, courses, gallery, alerts — on the same tenant as ERP. Offer website only, ERP only, or both.',
    outcome: 'Public site on the same tenant as ERP',
    stats: [
      { label: 'Pages live', value: '18' },
      { label: 'Gallery', value: '64' },
      { label: 'Alerts', value: '3' },
    ],
    rows: [
      { primary: 'Home · Admissions open', secondary: 'Custom domain ready', badge: 'Live' },
      { primary: 'Courses & facilities', secondary: 'CMS edit · publish', badge: 'Updated' },
      { primary: 'Holiday alert banner', secondary: 'Visible on public site', badge: 'Pinned' },
    ],
  },
  {
    id: 'ai',
    category: 'reach',
    label: 'AI question bank',
    short: 'AI bank',
    blurb: 'Generate questions with AI, save into a school-owned bank, and reuse next to exams — not one-off ChatGPT paste.',
    outcome: 'School-owned bank, not one-off paste',
    stats: [
      { label: 'Bank items', value: '1,120' },
      { label: 'Subjects', value: '9' },
      { label: 'Sets this week', value: '48' },
    ],
    rows: [
      { primary: 'Algebra · Quadratic', secondary: 'Class 10 · 20 Qs · saved', badge: 'Bank' },
      { primary: 'Biology · Cells', secondary: 'Class 9 · AI draft', badge: 'Draft' },
      { primary: 'History · Freedom', secondary: 'Class 8 · reused from bank', badge: 'Ready' },
    ],
  },
  {
    id: 'analytics',
    category: 'platform',
    label: 'Teaching analytics',
    short: 'Analytics',
    blurb: 'See teaching outcomes by class and subject — spot growth and classes that deserve a closer look.',
    outcome: 'Class and subject outcomes at a glance',
    stats: [
      { label: 'Classes tracked', value: '86' },
      { label: 'Teaching well', value: '41%' },
      { label: 'Needs review', value: '3' },
    ],
    rows: [
      { primary: 'Ms. Mehta · Mathematics', secondary: '10-A · strong term', badge: 'Well' },
      { primary: 'Mr. Das · Mathematics', secondary: '8-C · needs support', badge: 'Watch' },
      { primary: 'Ms. Bose · Social Science', secondary: '7-A · review suggested', badge: 'Review' },
    ],
  },
  {
    id: 'multicampus',
    category: 'platform',
    label: 'Multi-campus admin',
    short: 'Campuses',
    blurb: 'Operate groups of schools from one console — each campus stays separate, with module and website control.',
    outcome: 'One console for a group of schools',
    stats: [
      { label: 'Campuses', value: '24' },
      { label: 'Active today', value: '21' },
      { label: 'New this month', value: '3' },
    ],
    rows: [
      { primary: 'Vikas Vidyalaya', secondary: 'ERP + website · live', badge: 'Live' },
      { primary: 'Sunrise Public', secondary: 'ERP only · live', badge: 'Live' },
      { primary: 'Green Valley', secondary: 'Website setup', badge: 'Setup' },
    ],
  },
];

const TAB_BASE = 'product-explorer';

export function ProductExplorer() {
  const [activeId, setActiveId] = useState<ModuleId>('admissions');
  const [categoryId, setCategoryId] = useState<CategoryId>('people');
  const railRef = useRef<HTMLDivElement>(null);

  const active = MODULES.find((m) => m.id === activeId) ?? MODULES[0];
  const categoryModules = MODULES.filter((m) => m.category === categoryId);
  const activeIndex = MODULES.findIndex((m) => m.id === active.id) + 1;
  const panelId = `${TAB_BASE}-panel`;
  const categoryMeta = CATEGORIES.find((c) => c.id === categoryId) ?? CATEGORIES[0];

  useEffect(() => {
    const el = railRef.current?.querySelector<HTMLButtonElement>(
      `[data-module-id="${activeId}"]`
    );
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [activeId, categoryId]);

  const selectModule = (id: ModuleId) => {
    const mod = MODULES.find((m) => m.id === id);
    if (!mod) return;
    setCategoryId(mod.category);
    setActiveId(id);
  };

  const selectCategory = (id: CategoryId) => {
    setCategoryId(id);
    const first = MODULES.find((m) => m.category === id);
    if (first) setActiveId(first.id);
  };

  const moveActive = (delta: number) => {
    const idx = MODULES.findIndex((m) => m.id === activeId);
    const next = MODULES[(idx + delta + MODULES.length) % MODULES.length];
    selectModule(next.id);
    window.requestAnimationFrame(() => {
      document.getElementById(`${TAB_BASE}-tab-${next.id}`)?.focus();
    });
  };

  return (
    <div className="product-explorer">
      <div className="product-explorer__nav">
        <div className="product-explorer__cats" role="tablist" aria-label="Product areas">
          {CATEGORIES.map((cat) => {
            const selected = cat.id === categoryId;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`product-explorer__cat${selected ? ' product-explorer__cat--active' : ''}`}
                onClick={() => selectCategory(cat.id)}
              >
                <span className="product-explorer__cat-label">{cat.label}</span>
                <span className="product-explorer__cat-hint">{cat.hint}</span>
              </button>
            );
          })}
        </div>

        <div
          ref={railRef}
          className="product-explorer__rail"
          role="tablist"
          aria-label={`${categoryMeta.label} modules`}
          id={`${TAB_BASE}-tabs`}
        >
          {categoryModules.map((mod) => {
            const selected = mod.id === active.id;
            const tabId = `${TAB_BASE}-tab-${mod.id}`;
            return (
              <button
                key={mod.id}
                type="button"
                role="tab"
                id={tabId}
                data-module-id={mod.id}
                aria-selected={selected}
                aria-controls={panelId}
                tabIndex={selected ? 0 : -1}
                className={`product-explorer__tab${selected ? ' product-explorer__tab--active' : ''}`}
                onClick={() => selectModule(mod.id)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                    e.preventDefault();
                    moveActive(1);
                  } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                    e.preventDefault();
                    moveActive(-1);
                  } else if (e.key === 'Home') {
                    e.preventDefault();
                    selectModule(MODULES[0].id);
                    document.getElementById(`${TAB_BASE}-tab-${MODULES[0].id}`)?.focus();
                  } else if (e.key === 'End') {
                    e.preventDefault();
                    const last = MODULES[MODULES.length - 1];
                    selectModule(last.id);
                    document.getElementById(`${TAB_BASE}-tab-${last.id}`)?.focus();
                  }
                }}
              >
                <span className="product-explorer__tab-short">{mod.short}</span>
                <span className="product-explorer__tab-outcome">{mod.outcome}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        key={active.id}
        className="product-explorer__panel"
        role="tabpanel"
        id={panelId}
        aria-labelledby={`${TAB_BASE}-tab-${active.id}`}
      >
        <div className="product-explorer__chrome" aria-hidden>
          <span className="product-explorer__chrome-dot" />
          <span className="product-explorer__chrome-dot" />
          <span className="product-explorer__chrome-dot" />
          <span className="product-explorer__chrome-path">skoolfix · {active.short.toLowerCase()}</span>
          <span className="product-explorer__chrome-count">
            {activeIndex} / {MODULES.length}
          </span>
        </div>

        <header className="product-explorer__head">
          <p className="product-explorer__eyebrow">{categoryMeta.label}</p>
          <h3 className="product-explorer__title">{active.label}</h3>
          <p className="product-explorer__blurb">{active.blurb}</p>
        </header>

        <div className="product-explorer__stats" aria-hidden>
          {active.stats.map((s, i) => (
            <div
              key={s.label}
              className="product-explorer__stat"
              style={{ animationDelay: `${0.06 + i * 0.05}s` }}
            >
              <span className="product-explorer__stat-value">{s.value}</span>
              <span className="product-explorer__stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="product-explorer__activity">
          <p className="product-explorer__activity-label">In the module</p>
          <ul className="product-explorer__rows">
            {active.rows.map((row, i) => (
              <li
                key={row.primary}
                className="product-explorer__row"
                style={{ animationDelay: `${0.12 + i * 0.06}s` }}
              >
                <span className="product-explorer__row-mark" aria-hidden />
                <div>
                  <p className="product-explorer__row-primary">{row.primary}</p>
                  <p className="product-explorer__row-secondary">{row.secondary}</p>
                </div>
                <span className="product-explorer__badge">{row.badge}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="product-explorer__footnote">
          Illustrative preview — your school data stays on your campus portal.
        </p>
      </div>
    </div>
  );
}
